import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { partialLeadSchema, type PartialLeadInput, type PlanService } from "@/lib/plan-wizard-schema";
import { useSavePartialLead, type PartialLeadContext, type SubmitResult } from "@/hooks/useSubmitEvent";
import { firstNameOf, planMenuPath } from "@/lib/plan-menus";
import { savePlanHandoff } from "@/lib/plan-handoff";

/**
 * Fire-and-forget partial capture (#226), shared by every wizard branch: once
 * name + email are valid, save the lead so it isn't lost if the visitor bails.
 * Runs at most once per mount and never blocks or surfaces errors.
 * `awaitId()` is what submit uses: clicking submit blurs the email field,
 * which starts the partial save in the same tick, so submit must wait for it
 * and upgrade that row, or both rows land and the orphan partial gets the
 * "finish your quote" nudge an hour later (#926).
 */
export function usePartialLeadCapture(
  getContact: () => Partial<PartialLeadInput>,
  ctx: PartialLeadContext,
) {
  const savePartial = useSavePartialLead();
  // Id of the captured partial; full submit upgrades this same row.
  const partialIdRef = useRef<string | null>(null);
  // The in-flight partial save, if any.
  const partialPromiseRef = useRef<Promise<string | null> | null>(null);
  const { event_type, source_page_slug, venue_interest } = ctx;

  const maybeSavePartial = useCallback(() => {
    if (partialIdRef.current || partialPromiseRef.current) return;
    const parsed = partialLeadSchema.safeParse(getContact());
    if (!parsed.success) return;
    partialPromiseRef.current = savePartial
      .mutateAsync({ event_type, source_page_slug, venue_interest, ...parsed.data })
      .then((id) => {
        partialIdRef.current = id;
        return id;
      })
      .catch((e) => {
        console.warn("[partial-lead] save failed", e);
        return null;
      });
  }, [savePartial, getContact, event_type, source_page_slug, venue_interest]);

  const awaitId = useCallback(async (): Promise<string | null> => {
    return partialIdRef.current ?? (await partialPromiseRef.current) ?? null;
  }, []);

  return { maybeSavePartial, awaitId };
}

/**
 * After a successful submit, send the visitor to their service's menu (#1221).
 * Lead tracking already fired inside the submit mutation, before it resolved.
 * The first name goes through sessionStorage, not the URL.
 */
export function usePlanMenuRedirect(service: PlanService) {
  const navigate = useNavigate();
  return useCallback(
    (result: SubmitResult, clientName: string) => {
      savePlanHandoff({ ref: result.event_number, firstName: firstNameOf(clientName) });
      navigate(planMenuPath(service, result.event_number), { replace: true });
      // The old thank-you scrolled to the top; the router doesn't restore scroll.
      window.scrollTo(0, 0);
    },
    [navigate, service],
  );
}
